import { Text, Stack } from "@certn/cascadia";
import { Card, BrandAsset } from "@certn/cascadia-react";
import { darkColors, lightColors } from "@certn/cascadia-tokens";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/css";
import { useBrandingContext } from "../BrandingContext";

export function Favicon() {
  const { t } = useTranslation();
  const { processedBrandingAssets } = useBrandingContext();

  return (
    <Stack orientation="vertical" gap="space-5" className={styles.container}>
      <Text style="global/heading-medium" className={styles.sampleHeading}>
        {t("Sample")}
      </Text>

      <Card className={styles.card}>
        <Text style="global/heading-medium" className={styles.heading}>
          {t("Light mode")}
        </Text>
        <div className={styles.browserTabs}>
          <div className={styles.trafficLights}>
            <div
              className={styles.trafficLight}
              style={{ backgroundColor: "#ff5f57" }}
            ></div>
            <div
              className={styles.trafficLight}
              style={{ backgroundColor: "#ffbd2e" }}
            ></div>
            <div
              className={styles.trafficLight}
              style={{ backgroundColor: "#28ca42" }}
            ></div>
          </div>
          <div className={styles.browserTab}>
            <div className={styles.tabContent}>
              <div className={styles.faviconContainer}>
                {processedBrandingAssets?.org_icon_url ? (
                  <img
                    src={processedBrandingAssets?.org_icon_url}
                    alt={t("Favicon")}
                    className={styles.tabFavicon}
                  />
                ) : (
                  <BrandAsset
                    brand="certn"
                    type="logo"
                    height={16}
                    width={20}
                    fill={lightColors["brand-certn-logo"]}
                  />
                )}
              </div>
            </div>
            <div className={styles.closeButton}>
              <span className={styles.closeIcon}>×</span>
            </div>
          </div>
          <span className={styles.newTabIcon}>+</span>
        </div>
      </Card>

      <Card className={css(styles.card, styles.cardDark)}>
        <Text style="global/heading-medium" className={styles.heading}>
          {t("Dark mode")}
        </Text>
        <div className={styles.browserTabsDark}>
          <div className={styles.trafficLights}>
            <div
              className={styles.trafficLight}
              style={{ backgroundColor: "#ff5f57" }}
            ></div>
            <div
              className={styles.trafficLight}
              style={{ backgroundColor: "#ffbd2e" }}
            ></div>
            <div
              className={styles.trafficLight}
              style={{ backgroundColor: "#28ca42" }}
            ></div>
          </div>
          <div className={styles.browserTabDark}>
            <div className={styles.tabContent}>
              <div className={styles.faviconContainer}>
                {processedBrandingAssets?.org_icon_url_dark_mode ? (
                  <img
                    src={processedBrandingAssets?.org_icon_url_dark_mode}
                    alt={t("Favicon")}
                    className={styles.tabFavicon}
                  />
                ) : (
                  <BrandAsset
                    brand="certn"
                    type="logo"
                    height={16}
                    width={20}
                    fill={darkColors["brand-certn-logo"]}
                  />
                )}
              </div>
            </div>
            <div className={styles.closeButton}>
              <span className={styles.closeIconDark}>×</span>
            </div>
          </div>
          <span className={styles.newTabIconDark}>+</span>
        </div>
      </Card>
    </Stack>
  );
}

const styles = {
  container: css({
    paddingBottom: "var(--space-5)",
    paddingLeft: "var(--space-3)",
    paddingRight: "var(--space-3)",
  }),
  card: css({
    padding: "var(--space-5)",
    border: "none",
    boxShadow: "none !important",
    outline: "none !important",
    "& *": {
      outline: "none !important",
    },
    "& > *": {
      outline: "none !important",
    },
  }),
  cardDark: css({
    border: "none !important",
    boxShadow: "none !important",
    outline: "none !important",
    "& *": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
    "& > *": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
  }),
  sampleHeading: css({
    marginBottom: "var(--space-4)",
    paddingLeft: "calc(var(--space-5) + var(--space-4))",
  }),
  heading: css({
    marginBottom: "var(--space-4)",
    paddingLeft: "var(--space-4)",
  }),
  browserTabs: css({
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    border: "1px solid #cccccc",
    borderRadius: "8px 8px 0 0",
    padding: "0",
    width: "100%",
    boxShadow: "none !important",
    position: "relative",
    marginTop: "var(--space-4)",
  }),
  browserTabsDark: css({
    display: "flex",
    alignItems: "center",
    backgroundColor:
      "var(--Background-Neutral-background-selected-strong-hover, rgba(36, 49, 52, 1))",
    border: "1px solid #cccccc",
    borderRadius: "8px 8px 0 0",
    padding: "0",
    width: "100%",
    boxShadow: "none !important",
    position: "relative",
    marginTop: "var(--space-4)",
  }),
  browserTab: css({
    display: "flex",
    alignItems: "center",
    backgroundColor: "#ffffff",
    border: "none !important",
    borderRadius: "8px 8px 0 0",
    padding: "8px 12px",
    marginTop: "8px",
    position: "relative",
    width: "200px",
  }),
  browserTabDark: css({
    display: "flex",
    alignItems: "center",
    backgroundColor:
      "var(--Background-Neutral-background-selected-medium, rgba(139, 146, 147, 1))",
    border: "none !important",
    outline: "none !important",
    boxShadow: "none !important",
    borderRadius: "8px 8px 0 0",
    padding: "8px 12px",
    marginTop: "8px",
    position: "relative",
    width: "200px",
  }),
  trafficLights: css({
    display: "flex",
    gap: "6px",
    padding: "8px 12px",
    alignItems: "center",
    height: "100%",
    justifyContent: "center",
  }),
  trafficLight: css({
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  }),
  tabContent: css({
    display: "flex",
    alignItems: "center",
    flex: 1,
    gap: "8px",
  }),
  faviconContainer: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "left",
    width: "56px",
    height: "16px",
  }),
  tabFavicon: css({
    width: "16px",
    height: "16px",
    objectFit: "contain",
  }),
  closeButton: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    cursor: "pointer",
    marginLeft: "8px",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
  }),
  closeIcon: css({
    color: "#666",
    fontSize: "20px",
    fontWeight: "300",
    lineHeight: "1",
    userSelect: "none",
  }),
  closeIconDark: css({
    color: "#999",
    fontSize: "20px",
    fontWeight: "300",
    lineHeight: "1",
    userSelect: "none",
  }),
  newTabIcon: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    backgroundColor: "#f5f5f5",
    border: "none !important",
    borderRadius: "0 8px 0 0",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    color: "#666",
    fontSize: "30px",
    fontWeight: "300",
    lineHeight: "1",
    userSelect: "none",
    "&:hover": {
      backgroundColor: "#e8e8e8",
    },
  }),
  newTabIconDark: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    backgroundColor: "#2d2d2d",
    border: "none !important",
    borderRadius: "0 8px 0 0",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    color: "#999",
    fontSize: "30px",
    fontWeight: "300",
    lineHeight: "1",
    userSelect: "none",
    "&:hover": {
      backgroundColor: "#3a3a3a",
    },
  }),
};
